import type { MapConfiguration } from '@/common/overmind/namespaces/arcgis/config/config.types';
import type {
  ContainmentResult,
  TimeInterval
} from '@/features/execute-calculation-set/overmind/calculation';
import { UnexpectedStateError } from '@/overmind/errors';

import { ConfigBuilder } from './ConfigBuilder';
import type { Api, ApiConfig } from './apiFactory';
import apiFactory from './apiFactory';
import type { CalculationSets } from './orchestratorApi';

let api: Api;
let clock: IKnowTime;

export default {
  initialize(config: ApiConfig, clockInput: IKnowTime) {
    api = apiFactory(config, 'Bearer');
    clock = clockInput;
  },
  setToken(newToken: string) {
    api.setToken(newToken);
  },
  setBasePath(basePath: string) {
    api.setBasePath(basePath);
  },
  getMapConfiguration(tenantId: string, controller?: AbortController) {
    if (!tenantId) {
      throw new UnexpectedStateError();
    }
    return api.get<MapConfiguration>(
      `${tenantId}/arcgis/config`,
      undefined,
      controller ? { signal: controller.signal } : undefined
    );
  },
  getArcGisToken(tenantId: string, controller?: AbortController) {
    if (!tenantId) {
      throw new UnexpectedStateError();
    }
    return api.get<string>(
      `${tenantId}/arcgis/token`,
      undefined,
      controller ? { signal: controller.signal } : undefined
    );
  },
  async getContainment(
    tenantId: string,
    circuitId: string,
    calculationSet: CalculationSets[number],
    arcgisToken?: string,
    controller?: AbortController
  ): Promise<ContainmentResult> {
    switch (calculationSet.type) {
      case 'LowVoltage':
        return await getLowVoltageContainment(
          tenantId,
          circuitId,
          arcgisToken,
          controller
        );
      case 'MediumVoltage':
        return await getMediumVoltageContainment(
          tenantId,
          circuitId,
          arcgisToken,
          controller
        );
    }
  }
};

type AsyncFunction<T> = (...args: any[]) => Promise<T>;

export type ContainmentResultWithExecutionTime =
  ResultWithExecutionTime<ContainmentResult>;
export type ResultWithExecutionTime<T> = {
  result: T;
  executionTime: TimeInterval;
};
export async function withExecutionTime<T>(
  fn: AsyncFunction<T>
): Promise<{ result: T; executionTime: TimeInterval }> {
  const startTime = clock.now().toISOString();
  const result = await fn();
  const endTime = clock.now().toISOString();

  const executionTime: TimeInterval = {
    startTime,
    endTime
  };

  return {
    result,
    executionTime
  };
}

export type IKnowTime = {
  now: () => Date;
};

export class MachineClock implements IKnowTime {
  now() {
    return new Date();
  }
}

export async function getContainment(
  tenantId: string,
  circuitId: string,
  calculationSet: CalculationSets[number],
  arcgisToken?: string,
  controller?: AbortController
): Promise<ContainmentResult> {
  switch (calculationSet.type) {
    case 'LowVoltage':
      return await getLowVoltageContainment(
        tenantId,
        circuitId,
        arcgisToken,
        controller
      );
    case 'MediumVoltage':
      return await getMediumVoltageContainment(
        tenantId,
        circuitId,
        arcgisToken,
        controller
      );
  }
}

function getLowVoltageContainment(
  tenantId: string,
  circuitId: string,
  arcgisToken?: string,
  controller?: AbortController
): Promise<ContainmentResult> {
  if (!tenantId) {
    throw new UnexpectedStateError();
  }
  const config = new ConfigBuilder()
    .withRequestAborting(controller)
    .withEsriAuthorizationHeader(arcgisToken)
    .build();

  return api.get<ContainmentResult>(
    `/tenants/${tenantId}/containment/low-voltage/by-related-object/${circuitId}`,
    undefined,
    config
  );
}

function getMediumVoltageContainment(
  tenantId: string,
  circuitId: string,
  arcgisToken?: string,
  controller?: AbortController
): Promise<ContainmentResult> {
  if (!tenantId) {
    throw new UnexpectedStateError();
  }
  const config = new ConfigBuilder()
    .withRequestAborting(controller)
    .withEsriAuthorizationHeader(arcgisToken)
    .build();

  return api.get<ContainmentResult>(
    `/tenants/${tenantId}/containment/medium-voltage/by-related-object/${circuitId}`,
    undefined,
    config
  );
}
